import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs, where, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { safeFirebaseQuery } from '../utils/firebaseHelper';
import { motion, AnimatePresence } from 'motion/react';
import { Calendar, Clock, MapPin, Users, ChefHat } from 'lucide-react';
import { ImageWithFallback } from '../components/ImageWithFallback';
import { SupperClubSEO } from '../components/PageSEO';
import { EventDetailModal } from '../components/EventDetailModal';
import LiveQA from '../components/LiveQA';
import './SupperClub.css';

const mockEvents = [
  {
    id: 'event1',
    title: 'Italian Masterclass with Chef Marco',
    chef: 'Chef Marco Rossi',
    restaurant: 'Bella Italia',
    date: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
    time: '19:00',
    location: 'Bella Italia, New York, NY',
    price: 85,
    capacity: 20,
    registered: 12,
    featuredDishes: ['dish1', 'dish5'],
    description: 'Join us for an exclusive Italian dining experience featuring authentic pasta and pizza. Chef Marco will prepare a 5-course meal showcasing traditional Italian techniques.',
    imageUrl: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800',
    menu: ['Antipasto Platter', 'Handmade Pasta', 'Wood-Fired Pizza', 'Tiramisu', 'Wine Pairing']
  },
  {
    id: 'event2',
    title: 'Sushi Omakase Experience',
    chef: 'Chef Yuki Tanaka',
    restaurant: 'Sakura Sushi',
    date: new Date(Date.now() + 21 * 24 * 60 * 60 * 1000),
    time: '18:30',
    location: 'Sakura Sushi, San Francisco, CA',
    price: 120,
    capacity: 12,
    registered: 8,
    featuredDishes: ['dish3'],
    description: 'An intimate omakase experience with premium ingredients flown in from Japan. Chef Yuki will craft a personalized menu based on the freshest catch of the day.',
    imageUrl: 'https://images.unsplash.com/photo-1579584425555-c3ce17fd4351?w=800',
    menu: ['Seasonal Sashimi', 'Nigiri Selection', 'Specialty Rolls', 'Miso Soup', 'Green Tea']
  },
  {
    id: 'event3',
    title: 'Farm-to-Table Summer Feast',
    chef: 'Chef Sarah Johnson',
    restaurant: 'Garden Fresh',
    date: new Date(Date.now() + 28 * 24 * 60 * 60 * 1000),
    time: '18:00',
    location: 'Garden Fresh, Portland, OR',
    price: 75,
    capacity: 25,
    registered: 18,
    featuredDishes: ['dish10'],
    description: 'Celebrate summer with a farm-to-table feast featuring locally sourced ingredients. Each dish highlights the season\'s best produce.',
    imageUrl: 'https://images.unsplash.com/photo-1546793665-c74683f339c1?w=800',
    menu: ['Seasonal Salad', 'Grilled Vegetables', 'Herb-Crusted Fish', 'Berry Tart', 'Local Wine']
  }
];

function SupperClub({ user }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('upcoming'); // 'upcoming', 'past', 'all'
  const [selectedEvent, setSelectedEvent] = useState(null);

  useEffect(() => {
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);


  const fetchEvents = async () => {
    try {
      setLoading(true);
      const eventsData = await safeFirebaseQuery(
        async () => {
          let q;
          if (filter === 'upcoming') {
            const now = Timestamp.now();
            q = query(
              collection(db, 'supperClubEvents'),
              where('date', '>=', now),
              orderBy('date', 'asc')
            );
          } else if (filter === 'past') {
            const now = Timestamp.now();
            q = query(
              collection(db, 'supperClubEvents'),
              where('date', '<', now),
              orderBy('date', 'desc')
            );
          } else {
            q = query(collection(db, 'supperClubEvents'), orderBy('date', 'asc'));
          }
          const querySnapshot = await getDocs(q);
          return querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
        },
        async () => {
          // Mock data fallback
          const now = new Date();
          let filtered = mockEvents;
          
          if (filter === 'upcoming') {
            filtered = mockEvents.filter(e => new Date(e.date) > now);
          } else if (filter === 'past') {
            filtered = mockEvents.filter(e => new Date(e.date) < now);
          }
          
          return filtered.sort((a, b) => new Date(a.date) - new Date(b.date));
        },
        'Error fetching events'
      );

      setEvents(eventsData);
    } catch (error) {
      console.error('Error fetching events:', error);
      // Use mock data as fallback
      const now = new Date();
      let filtered = mockEvents;
      if (filter === 'upcoming') {
        filtered = mockEvents.filter(e => new Date(e.date) > now);
      } else if (filter === 'past') {
        filtered = mockEvents.filter(e => new Date(e.date) < now);
      }
      setEvents(filtered);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    const d = date instanceof Date ? date : date.toDate();
    return d.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getDaysUntil = (date) => {
    const d = date instanceof Date ? date : date.toDate();
    const now = new Date();
    const diff = d.getTime() - now.getTime();
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return days;
  };


  if (loading) {
    return (
      <div className="supperclub-container">
        <div className="loading">Loading events...</div>
      </div>
    );
  }

  return (
    <>
      <SupperClubSEO />
      <div className="container mx-auto px-8">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20 pt-8"
        >
          <div className="w-1 h-20 bg-border mx-auto mb-8"></div>
          <h1 className="serif text-7xl mb-6">Supper Club</h1>
          <p className="text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Join exclusive dining experiences at top-rated restaurants. Meet chefs, taste exceptional dishes, and connect with fellow food lovers.
          </p>
        </motion.div>

        {/* Filters */}
        <div className="flex items-center justify-center gap-8 mb-16 border-t border-b border-border py-6">
          {[
            { id: 'upcoming', label: 'Upcoming Events' },
            { id: 'past', label: 'Past Events' },
            { id: 'all', label: 'All Events' },
          ].map((filterOption) => (
            <button
              key={filterOption.id}
              onClick={() => setFilter(filterOption.id)}
              className={`
                text-sm uppercase tracking-widest transition-colors relative pb-1
                ${filter === filterOption.id 
                  ? 'text-foreground' 
                  : 'text-muted-foreground hover:text-foreground'
                }
              `}
            >
              {filterOption.label}
              {filter === filterOption.id && (
                <motion.div
                  layoutId="filterIndicator"
                  className="absolute -bottom-6 left-0 right-0 h-px bg-foreground"
                  transition={{ type: 'spring', bounce: 0, duration: 0.4 }}
                />
              )}
            </button>
          ))}
        </div>

        {/* Events Grid */}
        {loading ? (
          <div className="text-center py-16">
            <p className="text-muted-foreground">Loading events...</p>
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-16">
            <h2 className="serif text-3xl mb-4">No events found</h2>
            <p className="text-muted-foreground">Check back soon for new supper club experiences!</p>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-8 mb-20">
            {events.map((event, index) => {
              const daysUntil = getDaysUntil(event.date);
              const isUpcoming = daysUntil > 0;
              const spotsLeft = event.capacity - (event.registered || 0);
              
              return (
                <motion.article
                  key={event.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="group cursor-pointer"
                  onClick={() => setSelectedEvent(event)}
                >
                  <div className="block">
                    {/* Image */}
                    <div className="relative aspect-[4/5] mb-6 overflow-hidden">
                      <ImageWithFallback
                        src={event.imageUrl}
                        alt={event.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      
                      {/* Days Away Badge */}
                      {isUpcoming && (
                        <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm px-4 py-2">
                          <p className="text-xs uppercase tracking-widest">
                            {daysUntil === 1 ? 'Tomorrow' : `${daysUntil} days away`}
                          </p>
                        </div>
                      )}

                      {/* Featured Badge */}
                      {spotsLeft <= 5 && spotsLeft > 0 && (
                        <div className="absolute top-4 right-4 bg-foreground text-background px-4 py-2">
                          <p className="text-xs uppercase tracking-widest">Only {spotsLeft} spots left</p>
                        </div>
                      )}

                      {spotsLeft === 0 && (
                        <div className="absolute inset-0 bg-black/80 flex items-center justify-center">
                          <p className="text-white text-xl uppercase tracking-widest">Sold Out</p>
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div>
                      <h3 className="serif text-3xl mb-4 group-hover:text-muted-foreground transition-colors">
                        {event.title}
                      </h3>

                      <div className="space-y-3 mb-6">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <ChefHat className="w-4 h-4" />
                          <span>{event.chef}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <MapPin className="w-4 h-4" />
                          <span>{event.restaurant}</span>
                        </div>
                      </div>

                      <div className="h-px bg-border mb-6"></div>

                      {/* Event Details */}
                      <div className="space-y-4 mb-6">
                        <div className="flex items-center gap-3">
                          <Calendar className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm">{formatDate(event.date)}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <Clock className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm">{event.time}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <Users className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm">{event.registered || 0} / {event.capacity} registered</span>
                        </div>
                      </div>

                      {/* Price & CTA */}
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Price</p>
                          <p className="serif text-2xl">${event.price}</p>
                        </div>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedEvent(event);
                          }}
                          className="px-6 py-3 border border-foreground text-foreground hover:bg-foreground hover:text-background transition-all duration-300 text-xs uppercase tracking-widest"
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.article>
              );
            })}
          </div>
        )}

        {/* Live Q&A Section */}
        <div className="mt-32 pt-16 border-t border-border">
          <LiveQA user={user} />
        </div>
      </div>

      {/* Event Detail Modal */}
      <AnimatePresence>
        {selectedEvent && (
          <EventDetailModal
            event={selectedEvent}
            user={user}
            onClose={() => setSelectedEvent(null)}
          />
        )}
      </AnimatePresence>
    </>
  );
}

export default SupperClub;

